import { gql } from '@apollo/client';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { CONDITIONS } from 'site-modules/shared/constants/appraisal/appraisal';

const CONDITION_ALIASES = [CONDITIONS.OUTSTANDING, CONDITIONS.CLEAN, CONDITIONS.AVERAGE, CONDITIONS.ROUGH].map(
  condition => condition.toLowerCase()
);

const ALL_CONDITIONS_QUERY = gql`
  query AllUsedTmvConditions(
    $styleId: String!
    $zipcode: String!
    $mileage: Int
    $colorId: String
    $optionIds: [String!]
  ) {
    style(styleId: $styleId) {
      outstanding: usedTmv(
        input: {
          zipcode: $zipcode
          mileage: $mileage
          colorId: $colorId
          optionSelection: { typical: false, optionIds: $optionIds }
          condition: OUTSTANDING
        }
      ) {
        ...UsedTmvConditionPrices
        lastChangeDate
      }
      clean: usedTmv(
        input: {
          zipcode: $zipcode
          mileage: $mileage
          colorId: $colorId
          optionSelection: { typical: false, optionIds: $optionIds }
          condition: CLEAN
        }
      ) {
        ...UsedTmvConditionPrices
      }
      average: usedTmv(
        input: {
          zipcode: $zipcode
          mileage: $mileage
          colorId: $colorId
          optionSelection: { typical: false, optionIds: $optionIds }
          condition: AVERAGE
        }
      ) {
        ...UsedTmvConditionPrices
      }
      rough: usedTmv(
        input: {
          zipcode: $zipcode
          mileage: $mileage
          colorId: $colorId
          optionSelection: { typical: false, optionIds: $optionIds }
          condition: ROUGH
        }
      ) {
        ...UsedTmvConditionPrices
      }
    }
  }
  fragment UsedTmvConditionPrices on UsedTmvDigest {
    adjustments {
      colorAdjustment {
        privateParty
        retail
        tradeIn
      }
      conditionAdjustment {
        privateParty
        retail
        tradeIn
      }
      mileageAdjustment {
        privateParty
        retail
        tradeIn
      }
      regionalAdjustment {
        privateParty
        retail
        tradeIn
      }
    }
    nationalBasePrice {
      privateParty
      retail
      tradeIn
    }
    totalWithOptions {
      privateParty
      retail
      tradeIn
    }
    optionTmvPrices {
      optionId
      price {
        privateParty
        retail
        tradeIn
      }
    }
    certifiedUsedPrice
  }
`;

const mapPrices = prices => ({
  usedPrivateParty: prices?.privateParty,
  usedTmvRetail: prices?.retail,
  usedTradeIn: prices?.tradeIn,
});

// Keep the REST contract so report, UPS, offer-event and SEO consumers share the same data.
export async function getAllUsedTmvConditions(
  { styleId, zipCode, mileage, colorId, optionIds },
  client = EdmundsGraphQLFederation,
  options = {}
) {
  const response = await client.query(
    ALL_CONDITIONS_QUERY,
    {
      styleId: String(styleId),
      zipcode: String(zipCode),
      mileage: typeof mileage === 'string' ? Number(mileage.replace(/\D/g, '')) : mileage,
      colorId: colorId ? String(colorId) : undefined,
      optionIds: optionIds ? String(optionIds).split(',').filter(Boolean) : [],
    },
    options
  );

  if (CONDITION_ALIASES.some(alias => !response?.style?.[alias])) {
    throw new Error('Incomplete used TMV conditions response');
  }

  return {
    tmvconditions: Object.fromEntries(
      CONDITION_ALIASES.map(alias => {
        const condition = response.style[alias];
        return [
          alias.toUpperCase(),
          {
            Current: {
              ...Object.fromEntries(
                ['colorAdjustment', 'conditionAdjustment', 'mileageAdjustment', 'regionalAdjustment'].map(name => [
                  name,
                  mapPrices(condition.adjustments?.[name]),
                ])
              ),
              nationalBasePrice: mapPrices(condition.nationalBasePrice),
              totalWithOptions: mapPrices(condition.totalWithOptions),
              optionTMVPrices: Object.fromEntries(
                (condition.optionTmvPrices || []).map(({ optionId, price }) => [optionId, mapPrices(price)])
              ),
              certifiedUsedPrice: condition.certifiedUsedPrice,
              lastChangeDate: response.style.outstanding.lastChangeDate,
            },
          },
        ];
      })
    ),
  };
}
